import React, { useEffect, useState } from "react";
import { api, fmtEur, fmtInt, MONTHS_ES } from "@/lib/api";
import { PageHeader, Section, Pill } from "@/components/ui-kit";
import { Package, Truck, Archive, TrendingUp, AlertCircle, Undo2 } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid } from "recharts";

const COLORS = ["#C65D3B", "#7D8C5A", "#D49A4D", "#4B5E6E", "#A64B4B"];

function KpiCard({ icon: Icon, label, value, sub, tone, testid }) {
  const tones = {
    brand: "text-brand", olive: "text-olive", amber: "text-amber", ink: "text-ink", crimson: "text-crimson",
  };
  return (
    <div className="bg-surface border border-line p-5 flex flex-col gap-2 rounded-sm" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-[0.12em] text-ink2">{label}</div>
        <Icon size={16} strokeWidth={1.5} className={tones[tone] || "text-ink2"} />
      </div>
      <div className="font-heading text-3xl text-ink leading-none mt-1 font-num">{value}</div>
      {sub && <div className="text-xs text-ink2">{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  const [k, setK] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/dashboard/kpis").then(({ data }) => setK(data)).catch((e) => setErr(e.message));
  }, []);

  if (err) return <div className="text-crimson">{err}</div>;
  if (!k) return <div className="text-ink2">Cargando panel…</div>;

  const chartData = (k.sales_by_month || []).map(s => {
    const [y, m] = s.month.split("-");
    return { name: `${MONTHS_ES[(parseInt(m) - 1) || 0].slice(0,3)} ${y.slice(2)}`, qty: s.qty, amount: s.amount };
  });

  const channelData = (k.sales_by_channel || []).map(c => ({ name: c.channel, value: c.qty }));

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        title="Panel de control"
        subtitle="Vista consolidada del stock, ventas y liquidaciones pendientes."
        testid="dashboard-header"
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiCard icon={Package} label="Stock propio" value={fmtInt(k.stock_propio)} sub="ejemplares en almacén" tone="brand" testid="kpi-stock-own" />
        <KpiCard icon={Truck} label="En distribuidor" value={fmtInt(k.stock_distribuidor)} sub="ejemplares en depósito distribuidor" tone="olive" testid="kpi-stock-distributor" />
        <KpiCard icon={Archive} label="En depósito cliente" value={fmtInt(k.stock_deposito_cliente)} sub="librerías · escuelas" tone="amber" testid="kpi-stock-consignment" />
        <KpiCard icon={AlertCircle} label="Liquidaciones abiertas" value={fmtInt(k.pending_settlements)} sub="pendientes de confirmar" tone="crimson" testid="kpi-pending-settlements" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <Section title="Ventas por mes" description="Últimos 6 meses · unidades vendidas" className="lg:col-span-2">
          <div className="p-4 h-64">
            {chartData.length === 0 ? (
              <div className="h-full flex items-center justify-center text-ink2 text-sm">Aún no hay ventas registradas</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid stroke="#E3DED5" strokeDasharray="2 2" vertical={false} />
                  <XAxis dataKey="name" tick={{ fill: "#5C6670", fontSize: 11 }} tickLine={false} axisLine={false} />
                  <YAxis tick={{ fill: "#5C6670", fontSize: 11 }} tickLine={false} axisLine={false} />
                  <Tooltip contentStyle={{ background: "#fff", border: "1px solid #E3DED5", borderRadius: 4, fontSize: 12 }} />
                  <Bar dataKey="qty" fill="#C65D3B" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </Section>

        <Section title="Ventas por canal" description="Distribución histórica">
          <div className="p-4 h-64 flex items-center justify-center">
            {channelData.length === 0 ? (
              <div className="text-ink2 text-sm">Sin datos</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={channelData} dataKey="value" nameKey="name" innerRadius={45} outerRadius={80} paddingAngle={2}>
                    {channelData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: "#fff", border: "1px solid #E3DED5", borderRadius: 4, fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
          <div className="px-5 pb-5 space-y-1">
            {channelData.map((c, i) => (
              <div key={c.name} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
                  <span className="capitalize text-ink">{c.name}</span>
                </div>
                <span className="font-mono text-ink2">{fmtInt(c.value)} uds</span>
              </div>
            ))}
          </div>
        </Section>
      </div>

      <Section title="Margen por título" description="Coste vs PVP (básico)">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[11px] uppercase tracking-[0.1em] text-ink2 bg-sidebar/50">
                <th className="text-left px-5 py-3 font-medium">ISBN</th>
                <th className="text-left px-5 py-3 font-medium">Título</th>
                <th className="text-right px-5 py-3 font-medium">Coste</th>
                <th className="text-right px-5 py-3 font-medium">PVP</th>
                <th className="text-right px-5 py-3 font-medium">Margen</th>
              </tr>
            </thead>
            <tbody>
              {k.margins.length === 0 && (
                <tr><td colSpan={5} className="text-center text-ink2 py-8">Añade libros al catálogo para ver el margen.</td></tr>
              )}
              {k.margins.map(m => (
                <tr key={m.book_id} className="border-t border-line hover:bg-sidebar/30">
                  <td className="px-5 py-3 font-mono text-xs text-ink2">{m.isbn}</td>
                  <td className="px-5 py-3">{m.title}</td>
                  <td className="px-5 py-3 text-right font-mono">{fmtEur(m.cost)}</td>
                  <td className="px-5 py-3 text-right font-mono">{fmtEur(m.pvp)}</td>
                  <td className="px-5 py-3 text-right">
                    <Pill tone={m.margin >= 0 ? "success" : "danger"}>{fmtEur(m.margin)}</Pill>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <div className="mt-6 grid grid-cols-2 gap-4">
        <KpiCard icon={Undo2} label="Devoluciones (histórico)" value={fmtInt(k.total_returns)} sub="unidades devueltas al almacén" tone="crimson" testid="kpi-returns" />
        <KpiCard icon={TrendingUp} label="Títulos activos" value={`${fmtInt(k.active_books)} / ${fmtInt(k.total_books)}`} sub="en catálogo" tone="olive" testid="kpi-active-books" />
      </div>
    </div>
  );
}
